/**
 * Defaults and types for the search-first homepage layout.
 */

export const HOMEPAGE_SEARCH_FIRST_FLAG = 'HOMEPAGE_SEARCH_FIRST_ENABLED';

export function isHomepageSearchFirstEnabled(
  env: NodeJS.ProcessEnv | Record<string, string | undefined> = process.env,
): boolean {
  return env.HOMEPAGE_SEARCH_FIRST_ENABLED === 'true';
}

export type HomepagePopularSearch = {
  id: string;
  label: string;
  query: string;
  sort_order: number;
};

export type HomepageSearchType =
  | 'events'
  | 'agencies'
  | 'venues'
  | 'instructors'
  | 'courses';

export const HOMEPAGE_SEARCH_TYPES: readonly HomepageSearchType[] = [
  'events',
  'instructors',
  'agencies',
  'venues',
  'courses',
] as const;

export type SearchHeroContent = {
  brand: string;
  mission: string;
  support: string;
  placeholder: string;
};

export type HostTrainingContent = {
  title: string;
  subtitle: string;
  bullets: string[];
  freeNote: string;
  primaryCtaLabel: string;
  primaryCtaHref: string;
  secondaryCtaLabel: string;
  secondaryCtaHref: string;
};

export type PathCardContent = {
  id: string;
  label: string;
  sentence: string;
  href: string;
  iconKey: 'search' | 'host' | 'instructor';
};

export type TrustPointContent = {
  id: string;
  label: string;
  sort_order: number;
};

export type HomepageTopicCategory = {
  id: string;
  label: string;
  search: string;
  iconKey:
    | 'patrol'
    | 'firearms'
    | 'traffic'
    | 'crisis'
    | 'leadership'
    | 'investigations'
    | 'defensive'
    | 'technology'
    | 'evoc'
    | 'k9';
};

export const DEFAULT_SEARCH_HERO: SearchHeroContent = {
  brand: 'American Police Training',
  mission: 'The National Platform for Law Enforcement Training',
  support:
    'Search verified training, discover instructors, host courses, and manage registrations—all in one place.',
  placeholder: 'Search "SFST Practitioner"',
};

/** Rotating search field examples for events (pause on focus). */
const HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES_EVENTS = [
  'Search "SFST Practitioner"',
  'Search "Field Training Officer"',
  'Search "Firearms Instructor"',
  'Search "Leadership"',
  'Search "Background Investigation"',
  'Search "Patrol Rifle"',
] as const;

/** Per-object-type rotating placeholders for the homepage search hero. */
export const HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES_BY_TYPE: Record<
  HomepageSearchType,
  readonly string[]
> = {
  events: HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES_EVENTS,
  instructors: [
    'Search "Firearms Instructor"',
    'Search "Field Training Officer"',
    'Search "SFST Instructor"',
    'Search instructor name or specialty',
    'Search "Defensive Tactics Instructor"',
  ],
  agencies: [
    'Search "Austin Police"',
    'Search agency name or city',
    'Search "Sheriff"',
    'Search "State Police"',
    'Search "Highway Patrol"',
  ],
  venues: [
    'Search venue name or city',
    'Search "Training Center"',
    'Search "Academy"',
    'Search "Range"',
    'Search "Conference Center"',
  ],
  courses: [
    'Search "SFST Practitioner"',
    'Search "Field Training Officer"',
    'Search "Patrol Rifle"',
    'Search "Background Investigation"',
    'Search "Crisis Intervention"',
  ],
};

/** @deprecated Prefer HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES_BY_TYPE[searchType]. Alias of events. */
export const HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES =
  HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES_BY_TYPE.events;

const DEFAULT_POPULAR_SEARCHES_EVENTS: Array<Omit<HomepagePopularSearch, 'id'>> = [
  { label: 'SFST Practitioner', query: 'SFST Practitioner', sort_order: 0 },
  { label: 'Firearms Instructor', query: 'Firearms Instructor', sort_order: 1 },
  { label: 'Field Training Officer', query: 'Field Training Officer', sort_order: 2 },
  { label: 'Active Shooter Response', query: 'Active Shooter Response', sort_order: 3 },
  { label: 'Background Investigation', query: 'Background Investigation', sort_order: 4 },
  { label: 'Defensive Tactics', query: 'Defensive Tactics', sort_order: 5 },
  { label: 'Crisis Intervention', query: 'Crisis Intervention', sort_order: 6 },
  { label: 'Leadership Development', query: 'Leadership Development', sort_order: 7 },
];

/** Per-object-type static popular-search fallbacks for the homepage search hero. */
export const DEFAULT_POPULAR_SEARCHES_BY_TYPE: Record<
  HomepageSearchType,
  Array<Omit<HomepagePopularSearch, 'id'>>
> = {
  events: DEFAULT_POPULAR_SEARCHES_EVENTS,
  instructors: [
    { label: 'Firearms Instructor', query: 'Firearms Instructor', sort_order: 0 },
    { label: 'SFST Instructor', query: 'SFST Instructor', sort_order: 1 },
    { label: 'Defensive Tactics Instructor', query: 'Defensive Tactics Instructor', sort_order: 2 },
    { label: 'FTO', query: 'FTO', sort_order: 3 },
    { label: 'K9 Handler Trainer', query: 'K9 Handler Trainer', sort_order: 4 },
    { label: 'Crisis Intervention', query: 'Crisis Intervention', sort_order: 5 },
    { label: 'Leadership', query: 'Leadership', sort_order: 6 },
    { label: 'Use of Force', query: 'Use of Force', sort_order: 7 },
  ],
  agencies: [
    { label: 'Police Department', query: 'Police Department', sort_order: 0 },
    { label: 'Sheriff', query: 'Sheriff', sort_order: 1 },
    { label: 'State Police', query: 'State Police', sort_order: 2 },
    { label: 'Highway Patrol', query: 'Highway Patrol', sort_order: 3 },
    { label: 'Campus Police', query: 'Campus Police', sort_order: 4 },
    { label: 'Constable', query: 'Constable', sort_order: 5 },
    { label: 'Federal', query: 'Federal', sort_order: 6 },
    { label: 'Transit Police', query: 'Transit Police', sort_order: 7 },
  ],
  venues: [
    { label: 'Training Center', query: 'Training Center', sort_order: 0 },
    { label: 'Academy', query: 'Academy', sort_order: 1 },
    { label: 'Range', query: 'Range', sort_order: 2 },
    { label: 'Conference Center', query: 'Conference Center', sort_order: 3 },
    { label: 'Community Center', query: 'Community Center', sort_order: 4 },
    { label: 'Hotel', query: 'Hotel', sort_order: 5 },
    { label: 'University', query: 'University', sort_order: 6 },
    { label: 'Fire Station', query: 'Fire Station', sort_order: 7 },
  ],
  courses: [
    { label: 'SFST Practitioner', query: 'SFST Practitioner', sort_order: 0 },
    { label: 'Field Training Officer', query: 'Field Training Officer', sort_order: 1 },
    { label: 'Patrol Rifle', query: 'Patrol Rifle', sort_order: 2 },
    { label: 'Background Investigation', query: 'Background Investigation', sort_order: 3 },
    { label: 'Crisis Intervention', query: 'Crisis Intervention', sort_order: 4 },
    { label: 'Defensive Tactics', query: 'Defensive Tactics', sort_order: 5 },
    { label: 'Leadership Development', query: 'Leadership Development', sort_order: 6 },
    { label: 'Active Shooter Response', query: 'Active Shooter Response', sort_order: 7 },
  ],
};

/** @deprecated Prefer DEFAULT_POPULAR_SEARCHES_BY_TYPE[searchType]. Alias of events. */
export const DEFAULT_POPULAR_SEARCHES = DEFAULT_POPULAR_SEARCHES_BY_TYPE.events;

export const DEFAULT_HOST_TRAINING: HostTrainingContent = {
  title: 'Host Training for Free',
  subtitle: "List your agency's training and manage the full lifecycle on American Police Training.",
  bullets: [
    'Publish Training',
    'Accept Registrations',
    'Manage Attendance',
    'Generate Rosters',
    'Setup Online Exams',
  ],
  freeNote: 'FREE for qualifying law enforcement training.',
  primaryCtaLabel: 'Start Hosting',
  primaryCtaHref:
    '/provider-signup?role=host&utm_source=homepage&utm_campaign=host_training&utm_medium=web',
  secondaryCtaLabel: 'See services',
  secondaryCtaHref:
    '/training-provider-services?utm_source=homepage&utm_campaign=host_training&utm_medium=web',
};

export const DEFAULT_PATH_CARDS: PathCardContent[] = [
  {
    id: 'find',
    label: 'Find Training',
    sentence: 'Search verified events and courses nationwide.',
    href: '/events',
    iconKey: 'search',
  },
  // `role=` splits the shared apply flow into the host and instructor doors
  // (see `src/shared/conversion/providerApplyLite.ts`).
  {
    id: 'host',
    label: 'Host Training',
    sentence: "Publish your agency's classes and manage registrations for free when training is free.",
    href: '/provider-signup?role=host&utm_source=homepage&utm_campaign=path_host&utm_medium=web',
    iconKey: 'host',
  },
  {
    id: 'instructor',
    label: 'Become an Instructor',
    sentence: 'Join as a training provider and reach officers nationwide.',
    href: '/provider-signup?role=instructor&utm_source=homepage&utm_campaign=path_instructor&utm_medium=web',
    iconKey: 'instructor',
  },
];

export const DEFAULT_TRUST_POINTS: TrustPointContent[] = [
  { id: '1', label: 'Thousands of agencies', sort_order: 0 },
  { id: '2', label: 'Verified instructors', sort_order: 1 },
  { id: '3', label: 'Free hosting for qualifying training', sort_order: 2 },
  { id: '4', label: 'Nationwide search', sort_order: 3 },
];

export const HOMEPAGE_TOPIC_CATEGORIES: HomepageTopicCategory[] = [
  { id: 'patrol', label: 'Patrol', search: 'Patrol', iconKey: 'patrol' },
  { id: 'firearms', label: 'Firearms', search: 'Firearms', iconKey: 'firearms' },
  {
    id: 'traffic',
    label: 'Traffic Enforcement',
    search: 'Traffic Enforcement',
    iconKey: 'traffic',
  },
  {
    id: 'crisis',
    label: 'Crisis Intervention',
    search: 'Crisis Intervention',
    iconKey: 'crisis',
  },
  { id: 'leadership', label: 'Leadership', search: 'Leadership', iconKey: 'leadership' },
  {
    id: 'investigations',
    label: 'Criminal Investigations',
    search: 'Criminal Investigations',
    iconKey: 'investigations',
  },
  {
    id: 'defensive',
    label: 'Defensive Tactics',
    search: 'Defensive Tactics',
    iconKey: 'defensive',
  },
  { id: 'technology', label: 'Technology', search: 'Technology', iconKey: 'technology' },
  {
    id: 'evoc',
    label: 'Emergency Vehicle Operations',
    search: 'Emergency Vehicle Operations',
    iconKey: 'evoc',
  },
  { id: 'k9', label: 'K-9', search: 'K-9', iconKey: 'k9' },
];

/** Legacy entity hubs — retained for explore-bar CMS override compatibility. */
export const DEFAULT_BROWSE_CATEGORY_LINKS = [
  { label: 'Events', href: '/events', open_new_tab: false, sort_order: 0 },
  { label: 'Agencies', href: '/agencies', open_new_tab: false, sort_order: 1 },
  { label: 'Instructors', href: '/instructors', open_new_tab: false, sort_order: 2 },
  { label: 'Venues', href: '/venues', open_new_tab: false, sort_order: 3 },
  { label: 'Courses', href: '/courses', open_new_tab: false, sort_order: 4 },
] as const;

export const SEARCH_FIRST_SECTION_DEFAULTS: Array<{
  section_name: string;
  title: string;
  subtitle: string;
  visible: boolean;
  sort_order: number;
}> = [
  {
    section_name: 'search_hero',
    title: 'Search',
    subtitle: DEFAULT_SEARCH_HERO.mission,
    visible: true,
    sort_order: 10,
  },
  {
    section_name: 'trust_points',
    title: 'Platform Scale',
    subtitle: '',
    visible: false,
    sort_order: 20,
  },
  {
    section_name: 'host_training',
    title: DEFAULT_HOST_TRAINING.title,
    subtitle: DEFAULT_HOST_TRAINING.subtitle,
    visible: true,
    sort_order: 30,
  },
  {
    section_name: 'featured_events',
    title: 'Upcoming Training',
    subtitle: 'Featured upcoming sessions across the United States',
    visible: true,
    sort_order: 40,
  },
  {
    section_name: 'explore_bar',
    title: 'Browse by Category',
    subtitle: '',
    visible: true,
    sort_order: 50,
  },
  {
    section_name: 'path_cards',
    title: 'Platform Benefits',
    subtitle: 'Everything you need to find, host, and deliver training',
    visible: true,
    sort_order: 60,
  },
  {
    section_name: 'recent_published_blog',
    title: 'Explore Our Articles',
    subtitle: 'A fresh selection of law enforcement training insights',
    visible: true,
    sort_order: 70,
  },
  {
    section_name: 'hero',
    title: 'Welcome to American Police Training',
    subtitle: 'Legacy hero slider',
    visible: false,
    sort_order: 100,
  },
  {
    section_name: 'stats',
    title: 'Our Impact',
    subtitle: '',
    visible: false,
    sort_order: 110,
  },
  {
    section_name: 'search',
    title: 'Find Training',
    subtitle: 'Legacy mid-page search',
    visible: false,
    sort_order: 120,
  },
  {
    section_name: 'why_choose_us',
    title: 'Why Choose Us',
    subtitle: 'Legacy brochure section',
    visible: false,
    sort_order: 130,
  },
  {
    section_name: 'partners',
    title: 'Our Partners',
    subtitle: '',
    visible: false,
    sort_order: 140,
  },
  {
    section_name: 'cta',
    title: 'Get Started',
    subtitle: 'Legacy bottom CTA',
    visible: false,
    sort_order: 150,
  },
];

export function buildHomepageSearchPath(params: {
  query: string;
  location?: string;
  searchType?: HomepageSearchType;
}): string {
  const searchType = params.searchType || 'events';
  let targetPath = '/events';
  if (searchType === 'agencies') targetPath = '/agencies';
  else if (searchType === 'venues') targetPath = '/venues';
  else if (searchType === 'instructors') targetPath = '/instructors';
  else if (searchType === 'courses') targetPath = '/courses';

  const qs = new URLSearchParams();
  const q = params.query.trim();
  // Agencies + Course Catalog directories read `q`; event/venue/instructor listings use `search`.
  // Using `search` for /agencies is ignored by the directory URL sync and gets stripped to /agencies.
  if (q) {
    qs.set(searchType === 'courses' || searchType === 'agencies' ? 'q' : 'search', q);
  }
  if (params.location) qs.set('location', params.location);
  qs.set('type', searchType);
  const queryString = qs.toString();
  return queryString ? `${targetPath}?${queryString}` : targetPath;
}

export function buildHomepageTopicPath(topic: Pick<HomepageTopicCategory, 'search'>): string {
  return buildHomepageSearchPath({ query: topic.search, searchType: 'events' });
}
